import React, { useState, useEffect } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import LoadingCircleIcon from '../../icons/LoadingCircle';
import {
  getAllCoaches,
  getCohortById,
  getSubscriptions,
  updateSubscription,
} from '../../services/apiService';
import { getFullName } from '../../utils';
import BulkAdd from './components/BulkAdd';
import CohortSelection from './components/CohortSelection';
import CoachSelection from './components/CoachSelection';
import { Backdrop, CircularProgress } from '@material-ui/core';
import AlmaCohortTable from './utils/almaCohortTable';

function AlmaXCohortDashboard() {
  const [data, setData] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const dataFetch = async () => {
    const allSubscriptions = await getSubscriptions({ almax: true });
    setData(allSubscriptions);
  };

  useEffect(() => {
    dataFetch();
  }, []);

  const editHandler = (row) => {
    setEditDialogOpen(true);
    setEditUser(row);
  };

  return (
    <div>
      <div className='flex items-center'>
        <h1 className='user-heading text-3xl font-semibold my-5'>
          AlmaX Cohort Dashboard
        </h1>
        <button
          className='text-gray-600 rounded border-2 border-gray-300 px-4 py-2 m-5 hover:bg-gray-300'
          onClick={() => setAddDialogOpen(true)}
        >
          Bulk add users to AlmaX
        </button>
      </div>
      <div className='bg-yellow-200 p-4 m-3'>
        <h3>
          It can take upto 5 mins to reflect the changes. Please do not make the
          changes again.
        </h3>
      </div>
      {data.length ? (
        <AlmaCohortTable rowData={data} editHandler={editHandler} />
      ) : (
        <div className='m-auto w-full mt-20 text-center'>
          <LoadingCircleIcon iconStyle='w-10 h-10 mr-2' />
          <h1>Loading</h1>
        </div>
      )}
      <BulkAdd
        dialogOpen={addDialogOpen}
        setDialogOpen={setAddDialogOpen}
        dataFetch={dataFetch}
      />
      <EditAlmaxCohort
        dialogOpen={editDialogOpen}
        setDialogOpen={setEditDialogOpen}
        dataFetch={dataFetch}
        editUser={editUser}
      />
    </div>
  );
}

export default AlmaXCohortDashboard;

function EditAlmaxCohort({ dialogOpen, setDialogOpen, dataFetch, editUser }) {
  const [almaxCohort, setAlmaxCohort] = useState(null);
  const [careerCoach, setCareerCoach] = useState(null);
  const [champion, setChampion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const toggleDialog = () => {
    setDialogOpen(false);
    setAlmaxCohort(null);
    setCareerCoach(null);
    setChampion(null);
  };

  const editCohort = async () => {
    await updateSubscription({
      userId: editUser.userId,
      courseType: almaxCohort.courseType,
      almaxCohortName: almaxCohort.name,
      almaxCohortId: almaxCohort.id,
      careerCoachId: careerCoach.id,
      championId: champion.id,
    });
    dataFetch();
    toggleDialog();
  };

  useEffect(() => {
    const loadDetails = async () => {
      setIsLoading(true);
      const promises = [];
      if (editUser?.almaxCohortId)
        promises.push(getCohortById(editUser.almaxCohortId));
      if (editUser?.careerCoachId)
        promises.push(getAllCoaches({ userId: editUser.careerCoachId }));
      if (editUser?.championId)
        promises.push(getAllCoaches({ userId: editUser.championId }));
      const [almaxCohortData, careerCoachData, championData] =
        await Promise.all(promises);
      setAlmaxCohort(almaxCohortData);
      setCareerCoach(careerCoachData && careerCoachData?.[0]?.User);
      setChampion(championData && championData?.[0]?.User);
      setIsLoading(false);
    };
    loadDetails();
  }, [dialogOpen]);

  return (
    <>
      {!isLoading && (
        <Dialog
          aria-labelledby='almax-dialog-title'
          open={dialogOpen}
          onClose={toggleDialog}
        >
          <DialogTitle id='almax-dialog-title'>
            Edit User AlmaX Cohort
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Select cohort to change existing AlmaX cohort
            </DialogContentText>
            <div className='flex flex-col gap-3'>
              <TextField
                disabled
                label='Username'
                variant='outlined'
                value={getFullName(editUser?.User) || ''}
              />
              <TextField
                disabled
                label='User ID'
                variant='outlined'
                value={editUser?.userId || ''}
              />
              <CohortSelection value={almaxCohort} setValue={setAlmaxCohort} />
              <CoachSelection
                coach={careerCoach}
                setCoach={setCareerCoach}
                label='Career Coach'
              />

              <CoachSelection
                coach={champion}
                setCoach={setChampion}
                label='Champion'
                courseType={almaxCohort?.courseType}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleDialog} color='primary'>
              Cancel
            </Button>
            <Button color='primary' onClick={editCohort}>
              Edit
            </Button>
          </DialogActions>
          <Backdrop open={isLoading} style={{ zIndex: 100 }}>
            <CircularProgress />
          </Backdrop>
        </Dialog>
      )}
    </>
  );
}
